import { SERVICES } from "@/constants";
import { API } from "@/constants/api";
import { BOOKING_FORM } from "@/constants/booking-form";
import type { ServiceCardData } from "@/types";
import type { BookingService, PendingBooking } from "@/types/booking";
import { format, getDay, isValid, parseISO } from "date-fns";
import { BOOKING_WINDOWS } from "@/constants/dates";
import { isValidPhoneNumber } from "libphonenumber-js";
import { z } from "zod";
import { getDateStringInTimeZone, resolveTimeZone } from "./timeZone";

const allServices = Object.values(SERVICES).flatMap((group) => group.services);

const isBookableService = (service: ServiceCardData) =>
  service.ctas.some((cta) => cta.action.startsWith("/booking"));

const bookableServices = allServices.filter(isBookableService);

const bookingNameSchema = z
  .string()
  .trim()
  .min(BOOKING_FORM.schema.fields.name.minLength, BOOKING_FORM.schema.fields.name.minLengthMessage)
  .max(BOOKING_FORM.schema.fields.name.maxLength, BOOKING_FORM.schema.fields.name.maxLengthMessage);

const bookingPhoneSchema = z
  .string()
  .trim()
  .refine(isValidPhoneNumber, { message: BOOKING_FORM.schema.fields.phone.invalidMessage });

const bookingEmailSchema = z.string().trim().email(BOOKING_FORM.schema.fields.email.invalidMessage);

export const isValidBookingDateString = (value: string) => {
  if (!BOOKING_FORM.schema.dateRegex.test(value)) {
    return false;
  }

  const parsedDate = parseISO(value);

  return isValid(parsedDate) && format(parsedDate, "yyyy-MM-dd") === value;
};

const bookingDateStringSchema = z
  .string()
  .trim()
  .regex(BOOKING_FORM.schema.dateRegex, BOOKING_FORM.schema.fields.date.invalidMessage)
  .refine(isValidBookingDateString, BOOKING_FORM.schema.fields.date.invalidMessage);

const bookingTimeSchema = z
  .string()
  .trim()
  .regex(BOOKING_FORM.schema.timeRegex, BOOKING_FORM.schema.fields.slot.invalidMessage);

const verificationCodeSchema = z
  .string()
  .trim()
  .regex(API.verifyToken.verificationCode.pattern, API.verifyToken.errors.invalidPayload);

const manualPaymentMethodSchema = z.enum(["whish", "neo"]);

export const bookingClientSchema = z.object({
  name: bookingNameSchema,
  phone: bookingPhoneSchema,
  email: bookingEmailSchema,
  date: z
    .date({ error: BOOKING_FORM.schema.fields.date.requiredMessage })
    .refine((value) => isValid(value), BOOKING_FORM.schema.fields.date.invalidMessage),
});

export const bookingRequestSchema = z
  .object({
    serviceId: z.string().trim().min(1, BOOKING_FORM.schema.fields.serviceId.requiredMessage),
    date: bookingDateStringSchema,
    start: bookingTimeSchema,
    end: bookingTimeSchema,
    timeZone: z.string().trim().optional(),
    name: bookingNameSchema,
    email: bookingEmailSchema,
    phone: bookingPhoneSchema,
  })
  .strict();

export const verifyTokenRequestSchema = z
  .object({
    verificationCode: verificationCodeSchema,
  })
  .strict();

export const manualPaymentRequestSchema = z
  .object({
    method: manualPaymentMethodSchema,
  })
  .strict();

export type BookingClientValues = z.infer<typeof bookingClientSchema>;
export type BookingRequest = z.infer<typeof bookingRequestSchema>;
export type VerifyTokenRequest = z.infer<typeof verifyTokenRequestSchema>;
export type ManualPaymentRequest = z.infer<typeof manualPaymentRequestSchema>;

export const getBookableServiceById = (serviceId: string) =>
  bookableServices.find((service) => service.id === serviceId) ?? null;

export const toBookingService = (service: ServiceCardData): BookingService => ({
  id: service.id,
  name: service.name,
  description: service.description,
  price: service.price,
  sessionLength: service.sessionLength,
});

export const isBookableDateString = (value: string, timeZone?: string | null) => {
  if (!isValidBookingDateString(value)) {
    return false;
  }

  const selectedDate = parseISO(value);
  const today = getDateStringInTimeZone(new Date(), resolveTimeZone(timeZone));

  if (value <= today) {
    return false;
  }

  // Closed days (null window) are not bookable.
  if (BOOKING_WINDOWS[getDay(selectedDate)] == null) {
    return false;
  }

  return true;
};

export const isPendingBookingExpired = (createdAt: number) =>
  Date.now() - createdAt > API.initiateBooking.pendingBookingTtl.milliseconds;

export const resetVerificationProtection = (booking: PendingBooking) => {
  booking.verificationAttempts = 0;
  booking.verificationLockedUntil = undefined;
};

export const normalizeVerificationProtection = (booking: PendingBooking, now = Date.now()) => {
  if (booking.verificationLockedUntil && booking.verificationLockedUntil <= now) {
    resetVerificationProtection(booking);
  }
};

export const isVerificationLocked = (booking: PendingBooking, now = Date.now()) =>
  Boolean(booking.verificationLockedUntil && booking.verificationLockedUntil > now);

export const registerFailedVerificationAttempt = (booking: PendingBooking, now = Date.now()) => {
  const nextAttempts = (booking.verificationAttempts ?? 0) + 1;

  if (nextAttempts >= API.verifyToken.security.maxFailedAttempts) {
    booking.verificationAttempts = 0;
    booking.verificationLockedUntil = Math.min(
      now + API.verifyToken.security.lockoutMilliseconds,
      booking.createdAt + API.initiateBooking.pendingBookingTtl.milliseconds,
    );

    return { locked: true };
  }

  booking.verificationAttempts = nextAttempts;

  return { locked: false };
};
